/**
 * Tabs. The open tab lives in the URL, not in state -- a person comparing
 * two extractions will want two tabs open, and a reload must survive (§10).
 *
 * Tabs are built from counts, never hardcoded: a tab with zero rows is not
 * rendered at all (§2.8). A hardcoded eight-tab bar mostly showing "No data"
 * is the failure mode to avoid.
 */
import { useRef, type ReactNode } from 'react'
import { cn } from '~/lib/cn.ts'

export type TabDef<T extends string = string> = {
  id: T
  label: ReactNode
  /** Rendered after the label, muted. Pass it already formatted -- the
   *  component has no locale and 4505 next to a ledger reading 4,505 is a
   *  small inconsistency that reads as carelessness. Omit rather than pass 0. */
  count?: ReactNode
  /** Present but empty: rendered, disabled, with a reason on hover. */
  disabled?: boolean
  title?: string
}

export function Tabs<T extends string>({
  tabs, value, onChange, className, right,
}: {
  tabs: TabDef<T>[]
  value: T
  onChange: (id: T) => void
  className?: string
  right?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)

  // Arrow keys move between tabs, which is what the roving-tabindex pattern
  // exists for and what a keyboard-first audience will reach for.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const enabled = tabs.filter((t) => !t.disabled)
    const i = enabled.findIndex((t) => t.id === value)
    if (i < 0) return
    let next = -1
    if (e.key === 'ArrowRight') next = (i + 1) % enabled.length
    else if (e.key === 'ArrowLeft') next = (i - 1 + enabled.length) % enabled.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = enabled.length - 1
    if (next < 0) return
    e.preventDefault()
    const t = enabled[next]!
    onChange(t.id)
    ref.current?.querySelector<HTMLElement>(`[data-tab="${t.id}"]`)?.focus()
  }

  return (
    <div
      className={cn(
        'flex items-end justify-between gap-4 border-b border-[var(--border)]',
        className,
      )}
    >
      {/* `scroll-x-hint` paints an edge shadow only while there is more tab
          bar off-screen -- on a phone the bar overflows and nothing else
          says so. It costs nothing when everything fits. */}
      <div
        ref={ref}
        role="tablist"
        onKeyDown={onKeyDown}
        className="scroll-x-hint -mb-px flex snap-x snap-proximity gap-0.5 overflow-x-auto overscroll-x-contain"
      >
        {tabs.map((t) => {
          const active = t.id === value
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              data-tab={t.id}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              disabled={t.disabled}
              title={t.title}
              onClick={() => onChange(t.id)}
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-1.5',
                'min-h-[var(--control-h-md)] scroll-ms-3 snap-start',
                'text-[length:var(--text-data)] transition-colors duration-75',
                'disabled:cursor-default disabled:opacity-45',
                active
                  ? 'border-[var(--accent)] font-medium text-[var(--text)]'
                  : 'border-transparent text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text)]',
              )}
            >
              {t.label}
              {t.count !== null && t.count !== undefined && (
                <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
                  {t.count}
                </span>
              )}
            </button>
          )
        })}
      </div>
      {right && <div className="pb-1.5">{right}</div>}
    </div>
  )
}

/** A segmented control. Same shape as tabs, but for a value, not a view. */
export function Segmented<T extends string>({
  options, value, onChange, size = 'md', className, ariaLabel, fill,
}: {
  options: { id: T; label: ReactNode; title?: string; disabled?: boolean }[]
  value: T
  onChange: (id: T) => void
  size?: 'sm' | 'md'
  className?: string
  ariaLabel?: string
  /** Stretch to the container and split it evenly between the options. */
  fill?: boolean
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'overflow-hidden rounded-[var(--radius-control)] border border-[var(--border)]',
        'bg-[var(--bg-raised)]',
        fill ? 'flex w-full' : 'inline-flex',
        className,
      )}
    >
      {options.map((o, i) => {
        const active = o.id === value
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.id)}
            className={cn(
              'inline-flex items-center justify-center whitespace-nowrap',
              fill && 'flex-1',
              'transition-colors duration-75 disabled:cursor-default disabled:opacity-40',
              'active:brightness-95 motion-reduce:active:brightness-100',
              // Segmented buttons sit flush against each other, so they get
              // no `.tap` expansion -- it would only steal from a neighbour.
              // The token height is the target instead.
              size === 'sm'
                ? 'h-[var(--control-h-sm)] min-w-[var(--control-h-sm)] px-2 text-[length:var(--text-micro)]'
                : 'h-[var(--control-h-md)] min-w-[var(--control-h-md)] px-3 text-[length:var(--text-tiny)]',
              i > 0 && 'border-s border-[var(--border)]',
              active
                ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-sunken)] hover:text-[var(--text)]',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
